import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Spliting it into separate tasks at least helps when there are multiple localization forms on the page.
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('scroll', this.#onCountryListScroll);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryName;
    form?.submit();
  };

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      form.submit();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput || CSS.supports('field-sizing', 'content')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    let result = '';
    if (before) {
      result += `<mark>${before}</mark>`;
    }
    result += match;
    if (after) {
      result += `<mark>${after}</mark>`;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute('hidden', !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    for (const countryEl of countryListItems) {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryList = localizationForm.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      if (countryList) countryList.addEventListener('scroll', this.#onCountryListScroll);
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryList?.removeEventListener('scroll', this.#onCountryListScroll);
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

/**
 * Mobile-only localization bottom sheet using Shopify localization forms.
 *
 * @typedef {object} SimedgeMobileLocalizationRefs
 * @property {HTMLButtonElement} trigger - Header trigger.
 * @property {HTMLDialogElement} dialog - Modal bottom sheet dialog.
 * @property {HTMLElement} sheet - Visible bottom sheet.
 * @property {HTMLElement} swipeHandle - Drag handle and title region.
 * @property {HTMLButtonElement} closeButton - Dialog close button.
 *
 * @extends {Component<SimedgeMobileLocalizationRefs>}
 */
class SimedgeMobileLocalizationComponent extends Component {
  requiredRefs = ['trigger', 'dialog', 'sheet', 'swipeHandle', 'closeButton'];

  /** @type {Promise<unknown> | null} */
  #closePromise = null;

  /** @type {MutationObserver | null} */
  #menuStyleObserver = null;

  /** @type {{ startX: number; startY: number; lastY: number; startTime: number; lastTime: number; active: boolean; cancelled: boolean } | null} */
  #swipeGesture = null;

  /** @type {Animation | null} */
  #swipeAnimation = null;

  #suppressRestoredFocusRing = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.sheet.addEventListener('touchstart', this.#handleSwipeStart, { passive: true, capture: true });
    this.refs.sheet.addEventListener('touchmove', this.#handleSwipeMove, { passive: false, capture: true });
    this.refs.sheet.addEventListener('touchend', this.#handleSwipeEnd, { passive: true, capture: true });
    this.refs.sheet.addEventListener('touchcancel', this.#handleSwipeCancel, { passive: true, capture: true });
    this.refs.dialog.addEventListener('pointerdown', this.#handlePointerDown, { capture: true });

    const header = this.closest('#header-component');
    if (header) {
      this.#menuStyleObserver = new MutationObserver(this.#handleMenuStyleChange);
      this.#menuStyleObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    this.refs.sheet?.removeEventListener('touchstart', this.#handleSwipeStart, true);
    this.refs.sheet?.removeEventListener('touchmove', this.#handleSwipeMove, true);
    this.refs.sheet?.removeEventListener('touchend', this.#handleSwipeEnd, true);
    this.refs.sheet?.removeEventListener('touchcancel', this.#handleSwipeCancel, true);
    this.refs.dialog?.removeEventListener('pointerdown', this.#handlePointerDown, true);
    this.#swipeAnimation?.cancel();
    this.#menuStyleObserver?.disconnect();
    this.#menuStyleObserver = null;
  }

  /**
   * Close the mobile navigation first, then show the localization dialog.
   */
  open = async () => {
    const { dialog, sheet, trigger } = this.refs;
    if (dialog.open || this.#closePromise || !this.#isDrawerMode()) return;

    const headerDrawer = document.querySelector('header-drawer.simedge-mobile-menu-component');
    const headerDrawerDetails = headerDrawer?.querySelector(':scope > details[open]');

    if (headerDrawerDetails && 'close' in headerDrawer && typeof headerDrawer.close === 'function') {
      await headerDrawer.close();
      if (headerDrawerDetails.hasAttribute('open') || !this.#isDrawerMode()) return;
    }

    sheet.style.removeProperty('animation');
    sheet.style.removeProperty('transform');
    trigger.removeAttribute('data-suppress-focus-ring');
    this.#suppressRestoredFocusRing = false;
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    syncLocalizationScrollLock();

    requestAnimationFrame(() => {
      const activeOption = dialog.querySelector('.simedge-mobile-localization__option.is-active');
      const focusTarget = activeOption instanceof HTMLElement ? activeOption : this.refs.closeButton;
      focusTarget.focus();
    });
  };

  /**
   * Animate the dialog out, restore state and return focus to the trigger.
   * @returns {Promise<unknown>}
   */
  close = () => {
    const { dialog, sheet, trigger } = this.refs;

    if (this.#closePromise) return this.#closePromise;
    if (!dialog.open) {
      trigger.setAttribute('aria-expanded', 'false');
      syncLocalizationScrollLock();
      return Promise.resolve();
    }

    sheet.style.removeProperty('animation');
    sheet.style.removeProperty('transform');
    dialog.setAttribute('data-closing', '');
    this.#closePromise = onAnimationEnd(
      dialog,
      this.#finishClose,
      { subtree: true }
    );

    return this.#closePromise;
  };

  #finishClose = () => {
    const { dialog, sheet, trigger } = this.refs;
    this.#swipeGesture = null;
    this.#swipeAnimation?.cancel();
    this.#swipeAnimation = null;
    sheet.style.removeProperty('transform');
    sheet.style.removeProperty('will-change');
    sheet.style.removeProperty('animation');
    dialog.close();
    dialog.removeAttribute('data-closing');
    trigger.setAttribute('aria-expanded', 'false');
    syncLocalizationScrollLock();

    if (this.#suppressRestoredFocusRing) {
      trigger.setAttribute('data-suppress-focus-ring', '');
      const clearSuppression = () => {
        trigger.removeAttribute('data-suppress-focus-ring');
        trigger.removeEventListener('blur', clearSuppression);
        trigger.removeEventListener('keydown', clearSuppression);
      };
      trigger.addEventListener('blur', clearSuppression);
      trigger.addEventListener('keydown', clearSuppression);
    }

    trigger.focus();
    this.#suppressRestoredFocusRing = false;
    this.#closePromise = null;
  };

  #handlePointerDown = () => {
    this.#suppressRestoredFocusRing = true;
  };

  #handleSwipeStart = (event) => {
    if (!this.refs.dialog.open || this.#closePromise || event.touches.length !== 1) return;

    const target = event.composedPath()[0];
    if (!(target instanceof Element)) return;
    if (target.closest('button, a, input, select, textarea')) return;

    const touch = event.touches[0];
    this.#suppressRestoredFocusRing = true;
    const sheetRect = this.refs.sheet.getBoundingClientRect();
    const handleRect = this.refs.swipeHandle.getBoundingClientRect();
    if (touch.clientY < sheetRect.top || touch.clientY > handleRect.bottom) return;

    this.#swipeGesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastY: touch.clientY,
      startTime: event.timeStamp,
      lastTime: event.timeStamp,
      active: false,
      cancelled: false,
    };
  };

  #handleSwipeMove = (event) => {
    const gesture = this.#swipeGesture;
    if (!gesture || gesture.cancelled || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    gesture.lastY = touch.clientY;
    gesture.lastTime = event.timeStamp;

    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 10 && Math.abs(deltaX) >= Math.abs(deltaY)) {
      gesture.cancelled = true;
      return;
    }

    if (deltaY < 0) {
      gesture.cancelled = true;
      return;
    }

    if (!gesture.active && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 10) return;

    if (!gesture.active) this.refs.sheet.style.animation = 'none';
    gesture.active = true;
    if (event.cancelable) event.preventDefault();
    const distance = Math.min(deltaY, window.innerHeight);
    this.refs.sheet.style.willChange = 'transform';
    this.refs.sheet.style.transform = `translate3d(0, ${distance}px, 0)`;
  };

  #handleSwipeEnd = (event) => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (!gesture?.active || gesture.cancelled) return;

    const endY = event.changedTouches?.[0]?.clientY ?? gesture.lastY;
    const distance = Math.max(endY - gesture.startY, 0);
    const duration = Math.max((event.timeStamp || gesture.lastTime) - gesture.startTime, 1);
    const velocity = distance / duration;

    if (distance >= 96 || (distance >= 42 && velocity >= 0.5)) {
      this.#closeFromSwipe(distance);
      return;
    }

    this.#resetSwipePosition(distance);
  };

  #handleSwipeCancel = () => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (gesture?.active) this.#resetSwipePosition(Math.max(gesture.lastY - gesture.startY, 0));
  };

  #closeFromSwipe(distance) {
    const { dialog, sheet } = this.refs;
    if (this.#closePromise || !dialog.open) return this.#closePromise ?? Promise.resolve();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose();
      return Promise.resolve();
    }

    const animation = sheet.animate(
      [
        { transform: `translate3d(0, ${distance}px, 0)` },
        { transform: 'translate3d(0, 100dvh, 0)' },
      ],
      { duration: 220, easing: 'cubic-bezier(0.55, 0, 1, 0.45)', fill: 'forwards' }
    );
    this.#swipeAnimation = animation;
    this.#closePromise = Promise.race([
      animation.finished.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 320)),
    ]).then(this.#finishClose);
    return this.#closePromise;
  }

  async #resetSwipePosition(distance) {
    const { sheet } = this.refs;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sheet.style.removeProperty('transform');
      sheet.style.removeProperty('will-change');
      return;
    }

    const animation = sheet.animate(
      [
        { transform: `translate3d(0, ${distance}px, 0)` },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
    this.#swipeAnimation = animation;
    await Promise.race([
      animation.finished.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 260)),
    ]);

    if (this.#swipeAnimation === animation) {
      animation.cancel();
      this.#swipeAnimation = null;
      sheet.style.removeProperty('transform');
      sheet.style.removeProperty('will-change');
    }
  }

  /**
   * Close only when the transparent dialog surface outside the sheet is clicked.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    if (event.composedPath()[0] === this.refs.dialog) this.close();
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.#suppressRestoredFocusRing = false;
    this.close();
  };

  #isDrawerMode() {
    return this.closest('#header-component')?.getAttribute('data-menu-style') === 'drawer';
  }

  #handleMenuStyleChange = () => {
    if (!this.#isDrawerMode()) this.close();
  };
}

/**
 * Desktop localization dialog using the same Shopify forms and option structure as the mobile sheet.
 *
 * @typedef {object} SimedgeDesktopLocalizationRefs
 * @property {HTMLButtonElement} trigger - Header trigger.
 * @property {HTMLDialogElement} dialog - Full-viewport modal surface.
 * @property {HTMLElement} panel - Anchored localization panel.
 * @property {HTMLButtonElement} closeButton - Dialog close button.
 * @property {HTMLInputElement} [countrySearch] - Optional country filter.
 * @property {HTMLElement} [countryOptions] - Country options container.
 * @property {HTMLElement} [countryEmpty] - Empty filter state.
 *
 * @extends {Component<SimedgeDesktopLocalizationRefs>}
 */
class SimedgeDesktopLocalizationComponent extends Component {
  requiredRefs = ['trigger', 'dialog', 'panel', 'closeButton'];

  /** @type {Promise<unknown> | null} */
  #closePromise = null;

  #desktopMedia = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.#desktopMedia.addEventListener('change', this.#handleViewportChange);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    this.#desktopMedia.removeEventListener('change', this.#handleViewportChange);
    window.removeEventListener('resize', this.#handleResize);
  }

  toggle = () => {
    return this.refs.dialog.open ? this.close() : this.open();
  };

  open = () => {
    const { dialog, trigger } = this.refs;
    if (dialog.open || this.#closePromise || !this.#desktopMedia.matches) return;

    this.#positionPanel();
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    syncLocalizationScrollLock();

    requestAnimationFrame(() => {
      const activeLanguage = dialog.querySelector(
        '.simedge-desktop-localization__options--languages .simedge-desktop-localization__option.is-active'
      );
      const focusTarget = activeLanguage instanceof HTMLElement ? activeLanguage : this.refs.closeButton;
      focusTarget.focus();
    });
  };

  close = () => {
    const { dialog, panel, trigger } = this.refs;

    if (this.#closePromise) return this.#closePromise;
    if (!dialog.open) {
      trigger.setAttribute('aria-expanded', 'false');
      syncLocalizationScrollLock();
      return Promise.resolve();
    }

    dialog.setAttribute('data-closing', '');
    this.#closePromise = onAnimationEnd(
      panel,
      () => {
        dialog.close();
        dialog.removeAttribute('data-closing');
        trigger.setAttribute('aria-expanded', 'false');
        this.#resetCountryFilter();
        syncLocalizationScrollLock();
        trigger.focus();
        this.#closePromise = null;
      },
      { subtree: false }
    );

    return this.#closePromise;
  };

  /**
   * Filter only the country rows; language choices remain untouched.
   * @param {InputEvent} event
   */
  filterCountries = (event) => {
    const { countrySearch: countrySearchRef, countryOptions, countryEmpty } = this.refs;
    const eventSource = event.composedPath()[0];
    const countrySearch = eventSource instanceof HTMLInputElement ? eventSource : countrySearchRef;
    if (!countrySearch || !countryOptions) return;

    const searchValue = normalizeString(countrySearch.value);
    const countryRows = countryOptions.querySelectorAll('[data-country-option]');
    let visibleCount = 0;

    countryRows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const matches = normalizeString(row.dataset.countrySearch ?? '').includes(searchValue);
      row.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    if (countryEmpty) countryEmpty.hidden = visibleCount !== 0;
  };

  /**
   * Close only when the full-screen dialog surface outside the panel is clicked.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    if (event.composedPath()[0] === this.refs.dialog) this.close();
  }

  #resetCountryFilter() {
    const { countrySearch, countryOptions, countryEmpty } = this.refs;
    if (countrySearch) countrySearch.value = '';
    countryOptions?.querySelectorAll('[data-country-option]').forEach((row) => {
      if (row instanceof HTMLElement) row.hidden = false;
    });
    if (countryEmpty) countryEmpty.hidden = true;
  }

  #positionPanel() {
    const triggerRect = this.refs.trigger.getBoundingClientRect();
    const header = document.querySelector('#header-component');
    const headerRect = header instanceof HTMLElement ? header.getBoundingClientRect() : null;
    const panelWidth = Math.min(440, window.innerWidth - 24);
    const panelLeft = Math.min(
      Math.max(12, triggerRect.right - panelWidth),
      window.innerWidth - panelWidth - 12
    );
    const panelTop = headerRect?.bottom ?? triggerRect.bottom;

    this.style.setProperty('--simedge-desktop-localization-left', `${panelLeft}px`);
    this.style.setProperty('--simedge-desktop-localization-top', `${panelTop}px`);
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handleViewportChange = (event) => {
    if (!event.matches) this.close();
  };

  #handleResize = () => {
    if (this.refs.dialog.open) this.#positionPanel();
  };
}

/**
 * Keep document scrolling aligned with every currently open lockable surface.
 */
function syncLocalizationScrollLock() {
  const hasOpenScrollLock =
    document.querySelector('details[scroll-lock][open], dialog[scroll-lock][open]') !== null;
  document.documentElement.toggleAttribute('scroll-lock', hasOpenScrollLock);
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}

if (!customElements.get('simedge-mobile-localization')) {
  customElements.define('simedge-mobile-localization', SimedgeMobileLocalizationComponent);
}

if (!customElements.get('simedge-desktop-localization')) {
  customElements.define('simedge-desktop-localization', SimedgeDesktopLocalizationComponent);
}
